import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { bySlug, COMPETITORS } from "../data/competitors";
import { BRAND } from "../lib/brand";
import Faq from "../components/Faq";
import Reveal from "../components/Reveal";
import NotFound from "./NotFound";
import { useSeo, faqLd, breadcrumbLd, softwareLd } from "../lib/seo";

const FEATURE_ROWS = [
  ["Works without an iPhone", true],
  ["No app download required", true],
  ["Free full result (no trial trap)", true],
  ["Detailed 8-metric breakdown", true],
  ["AI Coach improvement plan", true],
  ["Future-Face projection", true],
  ["Progress tracking over time", true],
  ["Public shareable result page", true],
  ["Auto-generated share image", true],
  ["Global leaderboard / battles", true],
];

export default function VsPage() {
  const { slug } = useParams();
  const c = bySlug(slug);

  const faqs = c ? [
    { q: `Is Aesthetiq a good ${c.name} alternative?`, a: `Yes. Aesthetiq does the same core job — AI facial analysis with a score — but runs in any browser with no download, gives a detailed 8-metric breakdown, a real AI Coach plan, progress tracking and shareable result pages. ${c.name} is ${c.platform.toLowerCase()}.` },
    { q: `Is Aesthetiq free?`, a: `Aesthetiq has a generous free tier with a full analysis and a shareable FaceCard. ${c.name} pricing is typically ${c.price}.` },
    { q: `${BRAND.name} vs ${c.name} — which is more accurate?`, a: `Both estimate facial geometry from your photo. Aesthetiq reports 8 distinct metrics with per-metric notes and a projected potential score, which makes tracking progress over time clearer.` },
    { q: `Can I switch from ${c.name} to Aesthetiq?`, a: `There's nothing to install — just open Aesthetiq in your browser, upload a selfie and get your result in seconds.` },
  ] : [];

  useSeo({
    title: c ? `Aesthetiq vs ${c.name} — ${c.name} alternative compared` : "Comparison",
    description: c ? `An honest Aesthetiq vs ${c.name} comparison. See where ${c.name} is strong, where Aesthetiq wins, full feature and pricing table, and FAQs. The best ${c.name} alternative on the web.` : "",
    path: `/vs/${slug}`,
    jsonLd: c ? [softwareLd, faqLd(faqs), breadcrumbLd([{ name: "Home", path: "/" }, { name: "Compare", path: "/vs" }, { name: `vs ${c.name}`, path: `/vs/${c.slug}` }])] : null,
  });

  if (!c) return <NotFound />;
  const others = COMPETITORS.filter((x) => x.slug !== c.slug).slice(0, 4);

  return (
    <div className="container py-12">
      <Reveal>
        <Link to="/vs" className="text-sm text-muted-foreground transition hover:text-foreground">← All comparisons</Link>
        <span className="mt-6 block label-eyebrow">Comparison</span>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl">{BRAND.name} <span className="text-muted-foreground">vs</span> {c.name}</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{c.tagline} Here's an honest look at how the two compare — and why Aesthetiq is the best <strong className="text-foreground">{c.name} alternative</strong> if you want web access, depth and a real plan.</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="vs-cta">
          Try Aesthetiq free <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>

      {/* Strong / win */}
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-xl border border-border bg-card p-6">
            <h2 className="font-display text-2xl">Where {c.name} is strong</h2>
            <ul className="mt-4 space-y-3">
              {c.strong.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />{s}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="h-full rounded-xl border border-primary/40 bg-card p-6">
            <h2 className="font-display text-2xl">Where {BRAND.name} wins</h2>
            <ul className="mt-4 space-y-3">
              {c.win.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{s}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* Feature table */}
      <Reveal>
        <div className="mt-10 overflow-hidden rounded-xl border border-border">
          <div className="grid grid-cols-[1fr_auto_auto] gap-4 bg-secondary/30 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span>Feature</span><span className="text-center text-primary">{BRAND.name}</span><span className="text-center">{c.name}</span>
          </div>
          {FEATURE_ROWS.map(([label], i) => {
            const compVal = competitorRow(c, label);
            return (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-t border-border bg-card px-5 py-3.5">
                <span className="text-sm">{label}</span>
                <span className="flex justify-center"><Check className="h-4 w-4 text-primary" /></span>
                <span className="flex justify-center">{compVal ? <Check className="h-4 w-4 text-muted-foreground" /> : <X className="h-4 w-4 text-muted-foreground/40" />}</span>
              </div>
            );
          })}
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-t border-border bg-card px-5 py-3.5">
            <span className="text-sm font-medium">Pricing</span>
            <span className="text-center text-sm text-primary">Free / $12 mo</span>
            <span className="text-center text-sm text-muted-foreground">{c.price}</span>
          </div>
        </div>
      </Reveal>

      {/* FAQ */}
      <section className="mt-14 max-w-3xl">
        <h2 className="font-display text-3xl">{BRAND.name} vs {c.name} — FAQ</h2>
        <div className="mt-6"><Faq faqs={faqs} testId="vs-faq" /></div>
      </section>

      {/* Other comparisons */}
      <div className="mt-14">
        <h2 className="font-display text-xl">More comparisons</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((o) => (
            <Link key={o.slug} to={`/vs/${o.slug}`} className="rounded-xl border border-border bg-card p-5 transition hover:border-primary/40">
              <p className="font-display text-lg">vs {o.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{o.platform}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function competitorRow(c, label) {
  // Deterministic honesty: competitors generally lack web/no-download/free-full/sharing/leaderboard/projection
  const lacks = [
    "Works without an iPhone",
    "No app download required",
    "Free full result (no trial trap)",
    "Future-Face projection",
    "Public shareable result page",
    "Auto-generated share image",
    "Global leaderboard / battles",
  ];
  if (c.platform.toLowerCase().includes("web")) {
    // web competitors do have web + sometimes sharing
    if (label === "Works without an iPhone" || label === "No app download required") return true;
  }
  return !lacks.includes(label);
}
